import {format, startOfWeek, subDays} from 'date-fns';
import exportToCSV from "../utils/ExportExcel";
import { loadFromStorage, useSaveToStorage } from "../utils";
import { useState, useMemo, useEffect } from "react"
import { useGetSavedBonusPercentages, useJobtrackBonus, useSaveBonusPercentages } from "../scripts/queries/useBonusQueries";
import { useSaveWeeklyRoles } from "../scripts/queries/useWeeklyRolesQueries";
import { NotificationManager } from "../scripts/functions/NotificationManager";

import {useReactTable, getCoreRowModel, getSortedRowModel} from '@tanstack/react-table';
import Text from "../components/Text";

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];
const WEEK_DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export const useJobtrackNormalizer = (theme) => {

    const persisted = loadFromStorage('BONUS_FILTERS') || {};
    const [search, setSearch] = useState(persisted.search || "");
    const [sorting, setSorting] = useState(persisted.sorting || []);
    const [previousWeek, setPreviousWeek] = useState(false);
    const [statusFilter, setStatusFilter] = useState(persisted.statusFilter || "");
    const [debounceSearch, setDebounceSearch] = useState(persisted.search || "");

    useSaveToStorage('BONUS_FILTERS', {search, statusFilter, sorting})
    //Previous week: requested up to last Sunday; the current week is the backend default
    const {data = EMPTY_ARRAY, refetch: fetchBonusData, isLoading: loading} = useJobtrackBonus(previousWeek ? format(subDays(startOfWeek(new Date(), {weekStartsOn: 1}), 1), 'yyyy-MM-dd') : undefined);
    const {mutate: savePercentages, isLoading: isSaving} = useSaveBonusPercentages();
    const {data: percentages = EMPTY_ARRAY, refetch: fetchPercentages} = useGetSavedBonusPercentages();
    const {mutate: saveRoles} = useSaveWeeklyRoles();

    //Sends the filled role Excel as base64; the backend reads and validates it
    const uploadRoleFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => saveRoles({file: reader.result.split(',')[1]}, {
            onSuccess: (res) => res?.warnings?.length && NotificationManager.push(`Rol guardado con ${res.warnings.length} advertencia(s): ${res.warnings.slice(0, 3).join('; ')}`, false)
        });
        reader.readAsDataURL(file);
    }

    useEffect(() => {
        const t = setTimeout(() => setDebounceSearch(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    //Daily bonus: 50% efficiency >= machine rate, 50% captura within the limits.
    //Both need clock hours and a resource; a machine without a saved rate fails the efficiency.
    //The previous week uses the thresholds saved for it (or for the latest week before it); otherwise the current ones
    const processedData = useMemo(() => {
        const weekKey = `SEMANA#${format(subDays(startOfWeek(new Date(), {weekStartsOn: 1}), 7), 'yyyy-MM-dd')}`;
        const saved = previousWeek ? percentages.filter(p => p.machine.startsWith('SEMANA#') && p.machine <= weekKey).sort((a, b) => a.machine.localeCompare(b.machine)).pop() : null;
        const limits = saved ?? percentages.find(p => p.machine === 'CAPTURA');
        const rates = saved?.rates ?? Object.fromEntries(percentages.map(p => [p.machine, p.efficiency]));
        return data.map(row => {
            const days = {};
            (row.dias || []).forEach(d => {
                const rate = rates[d.maquina];
                const valid = d.horasReloj > 0 && d.maquina && d.maquina !== 'N/A';
                const efficiencyOk = valid && rate != null && d.eficiencia >= rate / 100;
                const capturaOk = valid && limits != null && d.captura >= Number(limits.capturaMin) / 100 && d.captura <= Number(limits.capturaMax) / 100;
                const i = (new Date(`${d.fecha}T00:00:00`).getDay() + 6) % 7; //Monday-based day of the week
                days[`bono${i}`] = (efficiencyOk ? 0.5 : 0) + (capturaOk ? 0.5 : 0);
                days[`captura${i}`] = capturaOk; //Tells where a 50% comes from
                days[`detalle${i}`] = `${d.maquina} · Eficiencia ${Math.round(d.eficiencia * 100)}% (rate ${rate ?? 'sin rate'}) · Captura ${Math.round(d.captura * 100)}%`;
            });
            return {...row, ...days};
        });
    }, [data, percentages, previousWeek])

    const filteredData = useMemo(() => {
        const searchTerm = debounceSearch.toLowerCase();
        return processedData.filter(item => {
            const name = (item.operador || "").toLowerCase();
            const machine = (item.maquina || "").toLowerCase();
            const nomina = String(item.nomina || "");

            const matchesSearch = !searchTerm || nomina.includes(searchTerm) || name.includes(searchTerm) || machine.includes(searchTerm);

            let matchesStatus = true;
            if (statusFilter === "__empty__") {
                matchesStatus = !item.status || item.status.trim() === "";
            } else if (statusFilter) {
                matchesStatus = item.status === statusFilter;
            }
            
            return matchesSearch && matchesStatus
        })
    }, [processedData, debounceSearch, statusFilter])

    const uniqueStatuses = useMemo(() => {
        const statuses = data.map(item => item.status).filter(Boolean);
        return [...new Set(statuses)]
    }, [data])
    
    const uniqueResources = useMemo(() => {
        //Every machine of the week, so all of them can get a rate in "Modificar recursos"
        const machines = data.flatMap(item => [item.maquina, ...(item.dias || []).map(d => d.maquina)]).filter(Boolean);
        return [...new Set(machines)];
    }, [data]);

    //Week shown: Monday to today, or the whole previous week
    const getBonusDate = () => {
        const end = previousWeek ? subDays(startOfWeek(new Date(), {weekStartsOn: 1}), 1) : new Date();
        return `${format(startOfWeek(end, {weekStartsOn: 1}), 'dd MMM')} - ${format(end, 'dd MMM yyyy')}`;
    }

    //100% without color, 0% red; 50% outlined in green when it comes from captura, blue when it comes from production
    const renderBonus = (value, detail, fromCaptura) => {
        if (value == null) return null;
        const color = value === 1 ? theme.ONED : value === 0 ? theme.Red : fromCaptura ? theme.SGreen : theme.Blue;
        return (
            <div title={detail} style={{display: 'inline-block', width: 'auto', borderRadius: '4px', padding: `1px 8px`, backgroundColor: value === 0 ? theme.LRed : 'transparent', border: value === 0.5 ? `1px solid ${color}` : 'none'}}>
                <Text color={color}>{`${value * 100}%`}</Text>
            </div>
        )
    }

    const columns = useMemo(() => [
        {accessorKey: 'nomina', header: 'No. Nómina'},
        {accessorKey: 'operador', header: 'Nombre'},
        {accessorKey: 'maquina', header: 'Recurso'},
        {accessorKey: 'status', header: 'Estado'},
        {accessorKey: 'turno', header: 'Turno'},
        ...WEEK_DAYS.map((day, i) => ({accessorKey: `bono${i}`, header: day, sortingFn: 'basic', cell: ({getValue, row}) => renderBonus(getValue(), row.original[`detalle${i}`], row.original[`captura${i}`])})),
    ], [theme]);
    
    const table = useReactTable({
        data: filteredData,
        columns,
        state:{sorting},
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const exportCSV = () => {
        const csvRows = [];

        const headers = columns.map((col) => col.header);
        csvRows.push(headers);

        const sortedRows = table.getSortedRowModel().rows.map(r => r.original);
        sortedRows.forEach((row) => {
            //Bonus days as 0, 50 or 100
            const values = columns.map(({accessorKey}) => {
                const value = row[accessorKey];
                return accessorKey.startsWith('bono') && value != null ? value * 100 : value ?? "";
            });
            csvRows.push(values);
        });

        const period = getBonusDate().replace(/\s+/g, "_").replace(/[^\w-]/g, "");
        //Same colors as the table: 0% red, 50% green (captura) or blue (production), 100% without color
        exportToCSV(`Bonos_${period}.xlsx`, csvRows, (r, c) => {
            const key = columns[c].accessorKey;
            const row = sortedRows[r - 1];
            if (r === 0 || !key.startsWith('bono') || row[key] == null || row[key] === 1) return;
            const color = row[key] === 0 ? 'FFC7CE' : row[`captura${key.slice(4)}`] ? 'C6EFCE' : 'BDD7EE';
            return {fill: {patternType: 'solid', fgColor: {rgb: color}}};
        })
    }

    return {table, loading, search, statusFilter, uniqueResources, uniqueStatuses, isSaving, percentages, fetchBonusData, fetchPercentages, savePercentages, getBonusDate, setSearch, exportCSV, setStatusFilter, uploadRoleFile, previousWeek, setPreviousWeek};
}